import { queryOptions } from "@tanstack/react-query";
import { $journalApi, fetchClient } from "../fetch/fetch-client";
import {
  getDraftJournals,
  GetDraftJournalsRequest,
} from "./get-draft-journals";
import type { components } from "./openapi/journal-service";

type SectionFourListResponse = components["schemas"]["SectionFourListResponse"] & {
  isNoContent?: boolean;
};

const SECTION_FOUR_NO_CONTENT: SectionFourListResponse = {
  isNoContent: true,
  section4List: [],
  total: 0,
};

const isHttpNoContent = (response: Response, data: unknown) => {
  if (response.status === 204) {
    return true;
  }

  if (response.status !== 200) {
    return false;
  }

  if (data == null || data === "") {
    return true;
  }

  return typeof data === "object" && !Array.isArray(data) && Object.keys(data).length === 0;
};

export const isSectionFourNoContent = (
  data: unknown,
): data is SectionFourListResponse & { isNoContent: true } =>
  Boolean(
    data &&
      typeof data === "object" &&
      (data as { isNoContent?: unknown }).isNoContent === true,
  );

const cleanQueryParams = (params: Record<string, any>) => {
  return Object.entries(params).reduce(
    (cleaned, [key, value]) => {
      if (value !== undefined && value !== "" && value !== null) {
        // eslint-disable-next-line no-param-reassign
        cleaned[key] = value;
      }
      return cleaned;
    },
    {} as Record<string, any>,
  );
};

export const journalsQueries = {
  rootKey: () => ["journals"],

  getDraftJournals: ({
    buildingObjectId,
    ...params
  }: GetDraftJournalsRequest) =>
    queryOptions({
      queryKey: [
        ...journalsQueries.rootKey(),
        "getDraftJournals",
        buildingObjectId,
        params,
      ],
      queryFn: () => getDraftJournals({ buildingObjectId, ...params }),
      // Списки черновиков по объекту: кэшируем между переходами (инвалидация из мутаций create/delete).
      staleTime: 60_000,
      gcTime: 5 * 60_000,
    }),

  getTitleJournal: ({ titleId }: { titleId: string }) => ({
    ...$journalApi.queryOptions("get", "/api/v1/journals/title/{titleId}", {
      // @ts-expect-error header добавляется для всех запросов на journal-service
      params: {
        path: { titleId },
      },
      gcTime: 0,
      staleTime: 0,
      cacheTime: 0,
    }),
  }),

  getSection1: ({
    sectionOneId,
    search,
    page,
    limit,
    dateFrom,
    dateTo,
    status,
  }: {
    sectionOneId: string;
    search?: string;
    page?: number;
    limit?: number;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
  }) => {
    const queryParams = cleanQueryParams({
      search,
      page,
      limit,
      dateFrom,
      dateStart: dateFrom,
      dateEnd: dateTo,
      status,
    });

    return {
      ...$journalApi.queryOptions(
        "get",
        "/api/v1/journals/section-one/{sectionOneId}",
        {
          params: {
            path: { sectionOneId },
            query: queryParams,
          },
        },
      ),
      gcTime: 0,
      staleTime: 0,
      cacheTime: 0,
    };
  },

  getSection2: ({
    sectionTwoId,
    search,
    page,
    limit,
    status,
    specialJournalDate,
    dateHandover,
    signerId,
  }: {
    sectionTwoId: string;
    search?: string;
    page?: number;
    limit?: number;
    status?: string;
    specialJournalDate?: string;
    dateHandover?: string;
    signerId?: string;
  }) => {
    const queryParams = cleanQueryParams({
      search,
      page,
      limit,
      status,
      specialJournalDate,
      dateHandover,
      signerId,
    });

    return {
      ...$journalApi.queryOptions(
        "get",
        "/api/v1/journals/section-two/{sectionTwoId}",
        {
          params: {
            path: { sectionTwoId },
            query: queryParams,
          },
        },
      ),
      gcTime: 0,
      staleTime: 0,
      cacheTime: 0,
    };
  },

  getSection3: ({
    sectionThreeListId,
    search,
    page,
    limit,
    dateFrom,
    dateTo,
    status,
    workType,
    actConnect,
    signerId,
    createDate,
    updateDate,
  }: {
    sectionThreeListId: string;
    search?: string;
    page?: number;
    limit?: number;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    workType?: string;
    actConnect?: string | boolean;
    signerId?: string;
    createDate?: string;
    updateDate?: string;
  }) => {
    const queryParams = cleanQueryParams({
      search,
      page,
      limit,
      dateFrom,
      workDateBegin: dateFrom,
      workDateEnd: dateTo,
      status,
      workType,
      actConnect,
      signerId,
      createDate,
      updateDate,
    });

    return {
      ...$journalApi.queryOptions(
        "get",
        "/api/v1/journals/section-three/{sectionThreeListId}/restriction",
        {
          params: {
            path: { sectionThreeListId },
            query: queryParams,
          },
        },
      ),
      enabled: !!sectionThreeListId,
      gcTime: 0,
      staleTime: 0,
      cacheTime: 0,
    };
  },

  getSection4: ({
    section4ListId,
    page,
    limit,
    search,
    dateFrom,
    dateTo,
    status,
    workType,
    actConnect,
    signerId,
    statusPartOne,
    statusPartTwo,
    defectFixingPlanDate,
    defectFixingFactDate,
    createDate,
    updateDate,
    isDefects,
  }: {
    section4ListId: string;
    page?: number;
    limit?: number;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    workType?: string;
    actConnect?: string;
    signerId?: string | string[];
    statusPartOne?: string[];
    statusPartTwo?: string[];
    defectFixingPlanDate?: string;
    defectFixingFactDate?: string;
    createDate?: string;
    updateDate?: string;
    isDefects?: string;
  }) => {
    const queryParams = cleanQueryParams({
      page,
      limit,
      search,
      dateFrom,
      dateTo,
      status,
      workType,
      actConnect,
      signerId,
      statusPartOne,
      statusPartTwo,
      defectFixingPlanDate,
      defectFixingFactDate,
      createDate,
      updateDate,
      isDefects,
    });

    const { queryKey } = $journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-four/{section4ListId}",
      {
        // @ts-expect-error query params section-four не совпадают с openapi типами
        params: {
          path: { section4ListId },
          query: queryParams,
        },
      },
    );

    return {
      queryKey,
      queryFn: async ({
        signal,
      }: {
        signal?: AbortSignal;
      }): Promise<components["schemas"]["SectionFourListResponse"]> => {
        try {
          const { data, error, response } = await fetchClient.GET(
            "/api/v1/journals/section-four/{section4ListId}",
            {
              // @ts-expect-error query params section-four не совпадают с openapi типами
              params: {
                path: { section4ListId },
                query: queryParams,
              },
              signal,
            },
          );

          if (isHttpNoContent(response, data)) {
            return { ...SECTION_FOUR_NO_CONTENT };
          }

          if (error) {
            throw error;
          }

          return data ?? { ...SECTION_FOUR_NO_CONTENT };
        } catch (error) {
          if (error instanceof SyntaxError) {
            return { ...SECTION_FOUR_NO_CONTENT };
          }

          throw error;
        }
      },
      gcTime: 0,
      staleTime: 0,
      cacheTime: 0,
    };
  },

  getSection6: ({
    sectionSixListId,
    search,
    page,
    limit,
    controlEventDate,
    defectsFixingPlanDateInfo,
    defectsFixingFactDateInfo,
    createDate,
  }: {
    sectionSixListId: string;
    search?: string;
    page?: number;
    limit?: number;
    controlEventDate?: string;
    defectsFixingPlanDateInfo?: string;
    defectsFixingFactDateInfo?: string;
    createDate?: string;
  }) => {
    const queryParams: Record<string, any> = cleanQueryParams({
      search,
      page,
      limit,
      controlEventDate,
      defectsFixingPlanDateInfo,
      defectsFixingFactDateInfo,
      createDate,
    });

    return {
      ...$journalApi.queryOptions(
        "get",
        "/api/v1/journals/section-six/{sectionSixListId}",
        {
          params: {
            path: { sectionSixListId },
            query: queryParams,
          },
        },
      ),
      gcTime: 0,
      staleTime: 0,
      cacheTime: 0,
    };
  },

  getSectionSixItem: ({
    sectionSixListId,
    listItemId,
  }: {
    sectionSixListId: string;
    listItemId: string;
  }) => {
    return {
      ...$journalApi.queryOptions(
        "get",
        "/api/v1/journals/section-six/{sectionSixListId}/listItem/{listItemId}",
        {
          params: {
            path: { sectionSixListId, listItemId },
          },
          cacheTime: 0,
          staleTime: 0,
          refetchOnMount: true,
          refetchOnWindowFocus: true,
        },
      ),
      enabled: !!sectionSixListId && !!listItemId,
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    };
  },

  getSectionThreeItem: ({ itemNoVersionId }: { itemNoVersionId: string }) => {
    return {
      ...$journalApi.queryOptions(
        "get",
        "/api/v1/journals/section-three/items/{itemNoVersionId}",
        {
          // @ts-expect-error query options cacheTime/refetch не совпадают с openapi типами
          params: {
            path: { itemNoVersionId },
          },
          cacheTime: 0,
          staleTime: 0,
          refetchOnMount: true,
          refetchOnWindowFocus: true,
        },
      ),
      enabled: !!itemNoVersionId,
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    };
  },

  getSectionFourItem: ({ itemNoVersionId }: { itemNoVersionId: string }) => {
    return {
      ...$journalApi.queryOptions(
        "get",
        "/api/v1/journals/section-four/items/{itemNoVersionId}",
        {
          // @ts-expect-error query options cacheTime/refetch не совпадают с openapi типами
          params: {
            path: { itemNoVersionId },
          },
          cacheTime: 0,
          staleTime: 0,
          refetchOnMount: true,
          refetchOnWindowFocus: true,
        },
      ),
      enabled: !!itemNoVersionId,
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    };
  },

  getJournal: ({ mainJournalId }: { mainJournalId: string }) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/main/{mainJournalId}",
      {
        params: {
          path: { mainJournalId },
        },
      },
    ),
    enabled: !!mainJournalId,
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
  }),

  getResponsibleAdminList: ({ mainJournalId }: { mainJournalId: string }) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/main/{mainJournalId}/admin",
      {
        params: {
          path: { mainJournalId },
        },
      },
    ),
    gcTime: 0,
    staleTime: 0,
    cacheTime: 0,
  }),

  getResponsibleList: (titleId: string) =>
    $journalApi.queryOptions(
      "get",
      "/api/v1/journals/title/{titleId}/responsibleList",
      {
        params: {
          path: { titleId },
        },
      },
    ),

  getResponsibleSectionOneList: (sectionOneId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-one/{sectionOneId}/responsibleList",
      { params: { path: { sectionOneId } } },
    ),
    enabled: !!sectionOneId,
    staleTime: 5 * 60_000,
  }),

  getResponsibleSectionTwoList: (sectionTwoId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-two/{sectionTwoId}/responsibleList",
      { params: { path: { sectionTwoId } } },
    ),
    enabled: !!sectionTwoId,
    staleTime: 5 * 60_000,
  }),

  getResponsibleSectionThreeList: (sectionThreeId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-three/{sectionThreeId}/responsibleList",
      { params: { path: { sectionThreeId } } },
    ),
    enabled: !!sectionThreeId,
    staleTime: 5 * 60_000,
  }),

  getResponsibleSectionFourList: (sectionFourId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-four/{sectionFourId}/responsibleList",
      { params: { path: { sectionFourId } } },
    ),
    enabled: !!sectionFourId,
    staleTime: 5 * 60_000,
  }),

  getResponsibleSectionSixList: (sectionSixId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-six/{sectionSixId}/responsibleList",
      { params: { path: { sectionSixId } } },
    ),
    enabled: !!sectionSixId,
    staleTime: 5 * 60_000,
  }),

  getResponsibleCommentList: (titleId: string) =>
    $journalApi.queryOptions(
      "get",
      "/api/v1/journals/title/{titleId}/responsibleList/comments",
      {
        params: {
          path: { titleId },
        },
      },
    ),
  getResponsibleCommentListFirstOne: (sectionOneId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-one/{sectionOneId}/responsibleList",
      { params: { path: { sectionOneId } } },
    ),
    enabled: !!sectionOneId,
    staleTime: 5 * 60_000,
  }),
  getResponsibleCommentListSecondOne: (sectionTwoId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-two/{sectionTwoId}/responsibleList",
      { params: { path: { sectionTwoId } } },
    ),
    enabled: !!sectionTwoId,
    staleTime: 5 * 60_000,
  }),

  getResponsibleCommentListThirdOne: (sectionThreeId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-three/{sectionThreeId}/responsibleList",
      { params: { path: { sectionThreeId } } },
    ),
    enabled: !!sectionThreeId,
    staleTime: 5 * 60_000,
  }),

  getResponsibleCommentListFourthOne: (sectionFourId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-four/{sectionFourId}/responsibleList",
      { params: { path: { sectionFourId } } },
    ),
    enabled: !!sectionFourId,
    staleTime: 5 * 60_000,
  }),

  getResponsibleCommentListFifthOne: (sectionFiveId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      // @ts-expect-error эндпоинт section-five responsibleList отсутствует в openapi-схеме
      "/api/v1/journals/section-five/{sectionFiveId}/responsibleList",
      { params: { path: { sectionFiveId } } },
    ),
    enabled: !!sectionFiveId,
    staleTime: 5 * 60_000,
  }),

  getResponsibleCommentListSixthOne: (sectionSixId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-six/{sectionSixId}/responsibleList",
      {
        params: { path: { sectionSixId } },
      },
    ),
    enabled: !!sectionSixId,
    staleTime: 5 * 60_000,
  }),
  getOriginalResponsibleList: (titleId: string) =>
    $journalApi.queryOptions(
      "get",
      "/api/v1/journals/title/{titleId}/responsibleList",
      {
        params: {
          path: { titleId },
        },
      },
    ),

  getJournalSignersList: (titleId: string) =>
    $journalApi.queryOptions("get", "/api/v1/journals/title/{titleId}/signs", {
      params: {
        path: { titleId },
      },
    }),

  getSectionOneSignersList: (sectionOneId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-one/{sectionOneId}/signs",
      {
        params: { path: { sectionOneId } },
      },
    ),
    enabled: !!sectionOneId,
    staleTime: 5 * 60_000,
  }),

  getSectionTwoSignersList: (sectionTwoId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-two/{sectionTwoId}/signs",
      { params: { path: { sectionTwoId } } },
    ),
    enabled: !!sectionTwoId,
    staleTime: 5 * 60_000,
  }),

  getSectionThreeSignersList: (sectionThreeId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-three/{sectionThreeId}/signs",
      { params: { path: { sectionThreeId } } },
    ),
    enabled: !!sectionThreeId,
    staleTime: 5 * 60_000,
  }),

  getSectionFourSignersList: (sectionFourId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-four/{sectionFourId}/signs",
      { params: { path: { sectionFourId } } },
    ),
    enabled: !!sectionFourId,
    staleTime: 5 * 60_000,
  }),

  getSectionSixSignersList: (sectionSixId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      // @ts-expect-error эндпоинт section-six signs отсутствует в openapi-схеме
      "/api/v1/journals/section-six/{sectionSixId}/signs",
      { params: { path: { sectionSixId } } },
    ),
    enabled: !!sectionSixId,
    staleTime: 5 * 60_000,
  }),

  getSectionFiveSignersList: (sectionFiveId: string) => ({
    ...$journalApi.queryOptions(
      "get",
      // @ts-expect-error эндпоинт section-five signs отсутствует в openapi-схеме
      "/api/v1/journals/section-five/{sectionFiveId}/signs",
      { params: { path: { sectionFiveId } } },
    ),
    enabled: !!sectionFiveId,
    staleTime: 5 * 60_000,
  }),

  getTitleNotes: ({
    mainJournalId,
    titleNotesListId,
    titleId,
  }: {
    mainJournalId: string;
    titleNotesListId: string;
    titleId: string;
  }) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/{mainJournalId}/title/{titleId}/titleNotesList/{titleNotesListId}",
      {
        params: {
          path: {
            mainJournalId,
            titleId,
            titleNotesListId,
          },
        },
      },
    ),
    gcTime: 0,
    staleTime: 0,
    cacheTime: 0,
  }),

  getTitleDetails: ({
    mainJournalId,
    registrationInfoId,
    titleId,
  }: {
    mainJournalId: string;
    registrationInfoId: string;
    titleId: string;
  }) => {
    return {
      ...$journalApi.queryOptions(
        "get",
        "/api/v1/journals/{mainJournalId}/title/{titleId}/registrationInfo/{registrationInfoId}",
        {
          params: {
            path: {
              mainJournalId,
              titleId,
              registrationInfoId,
            },
          },
        },
      ),
      gcTime: 0,
      staleTime: 0,
      cacheTime: 0,
    };
  },
  getTitleNotesXmlList: ({
    titleNotesListId,
  }: {
    titleNotesListId: string;
  }) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/title/titleNotesList/{titleNotesListId}/inputXmlList",
      {
        params: {
          path: {
            titleNotesListId,
          },
        },
      },
    ),
    gcTime: 0,
    staleTime: 0,
    cacheTime: 0,
  }),
  getTitleVersion: ({ titleVersionId }: { titleVersionId: string }) => ({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/titleVersion/{titleVersionId}",
      {
        params: {
          path: {
            titleVersionId,
          },
        },
      },
    ),
    // placeholderData: keepPreviousData,
    gcTime: 0,
    staleTime: 0,
    cacheTime: 0,
  }),
};
